// ─── Arvenzo theme JS ────────────────────────────────────────────────────────
(function () {
  'use strict';

  // ─── Header scroll state ───────────────────────────────────────────────────
  const header = document.querySelector('.site-header');
  if (header) {
    const onScroll = () => {
      const scrolled = window.scrollY > 50;
      header.classList.toggle('scrolled', scrolled);
      document.body.classList.toggle('scrolled', scrolled);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ─── Mobile menu ───────────────────────────────────────────────────────────
  const mobileToggle = document.querySelector('[data-mobile-toggle]');
  const mobileMenu = document.querySelector('.mobile-menu');
  if (mobileToggle && mobileMenu) {
    mobileToggle.addEventListener('click', () => {
      const isOpen = mobileMenu.classList.toggle('open');
      document.body.classList.toggle('drawer-open', isOpen);
      mobileToggle.setAttribute('aria-expanded', String(isOpen));
    });
    mobileMenu.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', () => {
        mobileMenu.classList.remove('open');
        document.body.classList.remove('drawer-open');
      });
    });
  }

  // ─── Cart drawer ───────────────────────────────────────────────────────────
  const cartDrawer = document.querySelector('.cart-drawer');
  const cartOverlay = document.querySelector('.cart-overlay');
  const cartBody = document.querySelector('[data-cart-body]');
  const cartFooter = document.querySelector('[data-cart-footer]');
  const cartCountEls = document.querySelectorAll('[data-cart-count]');

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = cartDrawer.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  function openCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('open');
    cartOverlay && cartOverlay.classList.add('open');
    document.body.classList.add('drawer-open');
    cartDrawer.focus();
    document.addEventListener('keydown', trapFocus);
  }
  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('open');
    cartOverlay && cartOverlay.classList.remove('open');
    document.body.classList.remove('drawer-open');
    document.removeEventListener('keydown', trapFocus);
  }

  document.querySelectorAll('[data-cart-open]').forEach(el =>
    el.addEventListener('click', e => { e.preventDefault(); openCart(); refreshCart(); })
  );
  document.querySelectorAll('[data-cart-close]').forEach(el =>
    el.addEventListener('click', e => { e.preventDefault(); closeCart(); })
  );
  cartOverlay && cartOverlay.addEventListener('click', closeCart);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeCart();
  });

  // ─── Cart API helpers ──────────────────────────────────────────────────────
  function formatMoney(cents) {
    const opts = { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' };
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-BE', opts).format(cents / 100);
    } catch (_) {
      return '€' + (cents / 100).toFixed(2);
    }
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return r.json();
  }

  async function addToCart(items) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!r.ok) throw new Error(await r.text());
    return r.json();
  }

  async function changeCartLine(id, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, quantity }),
    });
    return r.json();
  }

  function updateCartCount(qty) {
    cartCountEls.forEach(el => {
      el.textContent = qty > 0 ? String(qty) : '';
      el.style.display = qty > 0 ? '' : 'none';
    });
  }

  async function refreshCart() {
    if (!cartBody) return;
    try {
      const cart = await fetchCart();
      renderCart(cart);
      updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (e) {
      console.error('cart refresh failed', e);
    }
  }

  function renderCart(cart) {
    const threshold = Number(window.__ARVENZO_FREE_SHIPPING__ || 50) * 100;
    const tSubtotal = window.__T__?.cart_subtotal || 'Subtotaal';
    const tCheckout = window.__T__?.cart_checkout || 'Afrekenen';
    const tContinue = window.__T__?.cart_continue || 'Verder winkelen';
    const tEmpty = window.__T__?.cart_empty || 'Je winkelmand is leeg';
    const tEmptySub = window.__T__?.cart_empty_sub || 'Begin met shoppen om producten toe te voegen.';
    const tShopNow = window.__T__?.cart_shop_now || 'Nu shoppen';
    const tFreeShip = window.__T__?.cart_free_ship || '🎉 Je komt in aanmerking voor gratis verzending!';
    const tTowardsPrefix = window.__T__?.cart_towards_prefix || 'Voeg nog ';
    const tTowardsSuffix = window.__T__?.cart_towards_suffix || ' toe voor gratis verzending.';

    if (cart.item_count === 0) {
      cartBody.innerHTML = `
        <div class="cart-drawer-empty">
          <div class="cart-drawer-empty-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
          </div>
          <div>
            <p style="font-family:Barlow,sans-serif;font-weight:600;color:var(--color-ink)">${tEmpty}</p>
            <p style="color:var(--color-muted);font-size:14px;margin-top:2px">${tEmptySub}</p>
          </div>
          <a href="/collections/all" class="btn btn-brown" data-cart-close>${tShopNow}</a>
        </div>`;
      if (cartFooter) cartFooter.innerHTML = '';
      return;
    }

    const items = cart.items.map(item => {
      const options = (item.options_with_values || []).map(o => o.value).join(' · ');
      return `
        <div class="cart-item" data-line-key="${item.key}">
          <a href="${item.url}" class="cart-item-img" data-cart-close>
            ${item.image ? `<img src="${item.image}" alt="${item.product_title}" width="72" height="72">` : ''}
          </a>
          <div class="cart-item-body">
            <div style="display:flex;gap:8px;align-items:flex-start;justify-content:space-between">
              <div style="min-width:0">
                <a href="${item.url}" class="cart-item-title" data-cart-close>${item.product_title}</a>
                <p class="cart-item-options">${options}</p>
              </div>
              <button class="cart-item-remove" data-cart-remove data-line-key="${item.key}" aria-label="Verwijder">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div class="cart-item-row">
              <div class="qty-control">
                <button data-qty-dec data-line-key="${item.key}" aria-label="−">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
                </button>
                <span>${item.quantity}</span>
                <button data-qty-inc data-line-key="${item.key}" aria-label="+">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
                </button>
              </div>
              <span class="cart-item-price">${formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </div>`;
    }).join('');

    cartBody.innerHTML = items;

    const shippingMsg = cart.total_price >= threshold
      ? tFreeShip
      : `${tTowardsPrefix}${formatMoney(threshold - cart.total_price)}${tTowardsSuffix}`;

    if (cartFooter) {
      cartFooter.innerHTML = `
        <div class="cart-drawer-subtotal">
          <span class="label">${tSubtotal}</span>
          <span class="value">${formatMoney(cart.total_price)}</span>
        </div>
        <p class="cart-shipping-note">${shippingMsg}</p>
        <a href="/checkout" class="btn btn-brown" style="width:100%;padding:16px 24px">${tCheckout} →</a>
        <button data-cart-close style="text-align:center;font-size:14px;color:var(--color-muted)">${tContinue}</button>
      `;
      cartFooter.querySelectorAll('[data-cart-close]').forEach(el =>
        el.addEventListener('click', closeCart)
      );
    }

    cartBody.querySelectorAll('[data-qty-inc]').forEach(btn =>
      btn.addEventListener('click', async e => {
        const key = btn.getAttribute('data-line-key');
        const line = cart.items.find(i => i.key === key);
        if (!line) return;
        await changeCartLine(key, line.quantity + 1);
        await refreshCart();
      })
    );
    cartBody.querySelectorAll('[data-qty-dec]').forEach(btn =>
      btn.addEventListener('click', async e => {
        const key = btn.getAttribute('data-line-key');
        const line = cart.items.find(i => i.key === key);
        if (!line) return;
        await changeCartLine(key, Math.max(0, line.quantity - 1));
        await refreshCart();
      })
    );
    cartBody.querySelectorAll('[data-cart-remove]').forEach(btn =>
      btn.addEventListener('click', async e => {
        const key = btn.getAttribute('data-line-key');
        await changeCartLine(key, 0);
        await refreshCart();
      })
    );
    cartBody.querySelectorAll('[data-cart-close]').forEach(el =>
      el.addEventListener('click', closeCart)
    );
  }

  // Add-to-cart form on product page
  document.querySelectorAll('form[data-product-form]').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const submitBtn = form.querySelector('button[type="submit"]');
      const fd = new FormData(form);
      const id = fd.get('id');
      const qty = Number(fd.get('quantity') || 1);
      if (!id) return;
      submitBtn && submitBtn.setAttribute('disabled', 'true');
      try {
        await addToCart([{ id: Number(id), quantity: qty }]);
        if (typeof fbq !== 'undefined') {
          fbq('track', 'AddToCart', {
            content_ids: [String(id)],
            content_type: 'product_variant',
            currency: window.Shopify?.currency?.active || 'EUR'
          });
        }
        await refreshCart();
        openCart();
      } catch (err) {
        alert('Er ging iets mis. Probeer het opnieuw.');
        console.error(err);
      } finally {
        submitBtn && submitBtn.removeAttribute('disabled');
      }
    });
  });

  // Quick-add buttons (product cards)
  document.querySelectorAll('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      const originalHTML = btn.innerHTML;
      btn.setAttribute('disabled', 'true');
      try {
        await addToCart([{ id: Number(variantId), quantity: 1 }]);
        if (typeof fbq !== 'undefined') {
          fbq('track', 'AddToCart', {
            content_ids: [String(variantId)],
            content_type: 'product_variant',
            currency: window.Shopify?.currency?.active || 'EUR'
          });
        }
        await refreshCart();
        btn.classList.add('added');
        btn.innerHTML = '✓ ' + (window.__T__?.added || 'Toegevoegd');
        openCart();
        setTimeout(() => {
          btn.classList.remove('added');
          btn.innerHTML = originalHTML;
        }, 2000);
      } catch (err) {
        console.error(err);
      } finally {
        btn.removeAttribute('disabled');
      }
    });
  });

  // Initial cart count
  if (window.__ARVENZO_CART_COUNT__ !== undefined) {
    updateCartCount(Number(window.__ARVENZO_CART_COUNT__));
  }

  // ─── Product gallery ───────────────────────────────────────────────────────
  document.querySelectorAll('[data-gallery]').forEach(gallery => {
    const images = gallery.querySelectorAll('.gallery-main img');
    const thumbs = gallery.querySelectorAll('.gallery-thumbs button');
    const dots = gallery.querySelectorAll('.gallery-dots button');
    const prev = gallery.querySelector('.gallery-arrow.prev');
    const next = gallery.querySelector('.gallery-arrow.next');
    const mainEl = gallery.querySelector('.gallery-main');
    let idx = 0;
    let touchStartX = 0;

    function show(i) {
      idx = (i + images.length) % images.length;
      images.forEach((img, n) => img.classList.toggle('active', n === idx));
      thumbs.forEach((t, n) => t.classList.toggle('active', n === idx));
      dots.forEach((d, n) => d.classList.toggle('active', n === idx));
    }

    thumbs.forEach((t, n) => t.addEventListener('click', () => show(n)));
    dots.forEach((d, n) => d.addEventListener('click', () => show(n)));
    prev && prev.addEventListener('click', () => show(idx - 1));
    next && next.addEventListener('click', () => show(idx + 1));

    if (mainEl && images.length > 1) {
      mainEl.addEventListener('touchstart', e => {
        touchStartX = e.touches[0].clientX;
      }, { passive: true });
      mainEl.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 40) show(dx < 0 ? idx + 1 : idx - 1);
      }, { passive: true });
    }
  });

  // ─── Sticky ATC bar ────────────────────────────────────────────────────────
  const mainAtcBtn = document.querySelector('.add-to-cart-btn');
  const stickyAtcBar = document.querySelector('.sticky-atc');
  const stickyAtcBtn = document.querySelector('[data-sticky-atc]');
  const stickyPriceEl = document.querySelector('[data-sticky-price]');

  if (mainAtcBtn && stickyAtcBar) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyAtcBar.classList.toggle('visible', !entry.isIntersecting);
        stickyAtcBar.setAttribute('aria-hidden', String(entry.isIntersecting));
      },
      { threshold: 0 }
    );
    observer.observe(mainAtcBtn);
  }

  if (stickyAtcBtn) {
    stickyAtcBtn.addEventListener('click', async () => {
      const variantId = stickyAtcBtn.getAttribute('data-variant-id');
      if (!variantId || stickyAtcBtn.disabled) return;
      stickyAtcBtn.disabled = true;
      try {
        await addToCart([{ id: Number(variantId), quantity: 1 }]);
        if (typeof fbq !== 'undefined') {
          fbq('track', 'AddToCart', {
            content_ids: [String(variantId)],
            content_type: 'product_variant',
            currency: window.Shopify?.currency?.active || 'EUR'
          });
        }
        await refreshCart();
        openCart();
      } catch (err) {
        console.error(err);
      } finally {
        stickyAtcBtn.disabled = false;
      }
    });
  }

  // ─── Variant picker on product page ────────────────────────────────────────
  document.querySelectorAll('[data-variant-picker]').forEach(picker => {
    const form = picker.closest('form[data-product-form]');
    const variantsEl = picker.querySelector('[data-variants-json]');
    if (!variantsEl) return;
    const variants = JSON.parse(variantsEl.textContent);
    const optionGroups = picker.querySelectorAll('[data-option]');
    const priceNow = document.querySelector('[data-price-now]');
    const priceWas = document.querySelector('[data-price-was]');
    const submitBtn = form?.querySelector('button[type="submit"]');
    const idInput = form?.querySelector('input[name="id"]');
    const gallery = document.querySelector('[data-gallery]');

    function getSelected() {
      const out = [];
      optionGroups.forEach(g => {
        const active = g.querySelector('[data-option-value].active');
        if (active) out.push(active.getAttribute('data-option-value'));
      });
      return out;
    }

    function findVariant(sel) {
      return variants.find(v => sel.every((val, i) => v.options[i] === val)) || null;
    }

    function updateLabels() {
      optionGroups.forEach(g => {
        const active = g.querySelector('[data-option-value].active');
        const label = g.querySelector('[data-option-selected]');
        if (label && active) label.textContent = active.getAttribute('data-option-value');
      });
    }

    function updatePrice(variant) {
      if (!variant || !priceNow) return;
      priceNow.textContent = formatMoney(variant.price);
      if (priceWas) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceWas.textContent = formatMoney(variant.compare_at_price);
          priceWas.style.display = '';
        } else {
          priceWas.style.display = 'none';
        }
      }
    }

    function updateGalleryToVariant(variant) {
      if (!gallery || !variant) return;
      const imgs = gallery.querySelectorAll('.gallery-main img');
      const thumbBtns = gallery.querySelectorAll('.gallery-thumbs button');
      const dots = gallery.querySelectorAll('.gallery-dots button');

      const featuredSrc = variant.featured_image;
      if (!featuredSrc) return;

      const featuredBase = featuredSrc.split('/').pop().split('?')[0].split('.')[0];
      const matchIdx = [...imgs].findIndex(img => {
        const src = img.getAttribute('src') || '';
        return src.includes(featuredBase);
      });
      if (matchIdx < 0) return;

      imgs.forEach((img, n) => img.classList.toggle('active', n === matchIdx));
      thumbBtns.forEach((t, n) => t.classList.toggle('active', n === matchIdx));
      dots.forEach((d, n) => d.classList.toggle('active', n === matchIdx));

      const colorOptionIndex = picker.querySelector('[data-option]')
        ? [...picker.querySelectorAll('[data-option]')].findIndex(g => {
            const name = g.dataset.option || g.dataset.optionName || '';
            return name.toLowerCase().includes('color') || name.toLowerCase().includes('kleur');
          })
        : -1;

      if (colorOptionIndex >= 0) {
        const selectedColor = variant.options[colorOptionIndex];
        const colorImages = new Set(
          variants
            .filter(v => v.options[colorOptionIndex] === selectedColor && v.featured_image)
            .map(v => v.featured_image)
        );
        if (variant.featured_image) colorImages.add(variant.featured_image);

        thumbBtns.forEach((btn, n) => {
          const img = btn.querySelector('img');
          if (!img) return;
          const src = img.getAttribute('src');
          const isCurrentColor = [...colorImages].some(ci => src && src.includes(ci.split('?')[0].split('/').pop().split('.')[0]));
          btn.style.display = isCurrentColor ? '' : 'none';
        });
      }
    }

    function syncForm() {
      const sel = getSelected();
      const variant = findVariant(sel);
      if (!variant) {
        submitBtn && (submitBtn.disabled = true);
        return;
      }
      idInput && (idInput.value = variant.id);
      updatePrice(variant);
      updateLabels();
      updateGalleryToVariant(variant);
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        submitBtn.textContent = variant.available
          ? (window.__T__?.add_to_cart || 'In winkelmand')
          : (window.__T__?.sold_out || 'Uitverkocht');
      }
      if (stickyAtcBtn) {
        stickyAtcBtn.setAttribute('data-variant-id', variant.id);
        stickyAtcBtn.disabled = !variant.available;
        stickyAtcBtn.textContent = variant.available
          ? (window.__T__?.add_to_cart || 'In winkelmand')
          : (window.__T__?.sold_out || 'Uitverkocht');
      }
      if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price);
      const buyNowBtn = document.querySelector('.btn-buy-now');
      if (buyNowBtn && variant) {
        buyNowBtn.href = `/cart/${variant.id}:1`;
        buyNowBtn.style.display = variant.available ? '' : 'none';
      }
    }

    picker.querySelectorAll('[data-option-value]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const group = btn.closest('[data-option]');
        group.querySelectorAll('[data-option-value]').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        syncForm();
      });
    });

    // Disable submit until variant is explicitly chosen
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = window.__T__?.choose_size || 'Kies je maat';
    }
    if (idInput) idInput.value = '';
  });

  // Newsletter (Shopify customer form already handles via POST; progressive enhancement)
})();



// ── Geo-aware language switcher ──
(function () {
  var GEO_LANGS = {
    BE: ['nl', 'fr', 'de'],
    LU: ['fr', 'de'],
    FI: ['fi', 'sv'],
    IE: ['en', 'ga'],
    MT: ['mt', 'en']
  };

  var widget = document.getElementById('geo-lang-widget');
  if (!widget) return;

  var country   = (widget.dataset.country || '').toUpperCase();
  var available = (widget.dataset.available || '').split(',').filter(Boolean);
  var countryLangs = GEO_LANGS[country];

  if (!countryLangs) return;
  var relevant = countryLangs.filter(function (c) {
    return available.indexOf(c) !== -1;
  });
  if (relevant.length < 2) return;

  widget.querySelectorAll('.geo-lang-option').forEach(function (btn) {
    if (relevant.indexOf(btn.dataset.iso) === -1) {
      btn.closest('li').style.display = 'none';
    }
  });

  widget.style.display = 'block';

  var btn      = document.getElementById('geo-lang-btn');
  var dropdown = document.getElementById('geo-lang-dropdown');

  btn.addEventListener('click', function (e) {
    e.stopPropagation();
    var open = dropdown.style.display === 'none';
    dropdown.style.display = open ? 'block' : 'none';
    btn.setAttribute('aria-expanded', String(open));
  });

  document.addEventListener('click', function () {
    dropdown.style.display = 'none';
    btn.setAttribute('aria-expanded', 'false');
  });
})();
